import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  Chip,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Collapse,
  IconButton,
  LinearProgress,
  Rating,
  Tooltip
} from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '../Component/LanguageProvider';
import { useNavigate } from 'react-router-dom';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EmailIcon from '@mui/icons-material/Email';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import ArchiveIcon from '@mui/icons-material/Archive';
import SearchIcon from '@mui/icons-material/Search';
import ChatIcon from '@mui/icons-material/Chat';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningIcon from '@mui/icons-material/Warning';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TimelineIcon from '@mui/icons-material/Timeline';

export const OurServicesPage = () => {
  const { currentLang, direction, isRTL } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Scroll animation
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: (i) => ({
      scale: 1,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        type: "spring",
        stiffness: 200,
        damping: 20
      }
    }),
    hover: {
      y: -5,
      boxShadow: '0 20px 40px rgba(102,126,234,0.2)',
      transition: { type: "spring", stiffness: 300 }
    }
  };

  const floatingAnimation = {
    y: [0, -20, 0],
    transition: {
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Service data with updated terminology - using (إعداد – مساعدة – دعم إداري)
  const servicesData = {
    en: [
      {
        id: 1,
        title: 'Legal Consultations',
        icon: GavelIcon,
        color: '#4361ee',
        lightColor: '#e0e7ff',
        shortDesc: 'Expert legal advice without court representation',
        longDesc: 'Providing legal opinions, document analysis, contract review, and clarifying legal procedures for clients.',
        details: [
          'Legal opinion provision',
          'Document analysis and study',
          'Contract review',
          'Clarifying legal procedures for clients'
        ],
        note: 'Does NOT include: Litigation, court appearances, client representation, or preparing legal memos.',
        
        stats: {
          consultationsHandled: 850,
          successRate: 97,
          avgTime: '3 days',
          clientSatisfaction: 4.9,
          experts: 12
        },
        features: [
          '24/7 online consultation available',
          'Free initial assessment',
          'Multi-language support (AR/EN)',
          'Confidentiality guaranteed'
        ],
        tags: ['Corporate', 'Individual', 'Startups'],
        testimonials: [
          { name: 'Ahmed M.', rating: 5, comment: 'Excellent service!' },
          { name: 'Sara K.', rating: 5, comment: 'Very professional' }
        ]
      },
      {
        id: 2,
        title: 'Financial Claims Settlement Support (Advisory)',
        icon: AccountBalanceIcon,
        color: '#f72585',
        lightColor: '#ffe0f0',
        shortDesc: 'Advisory support for amicable dispute resolution',
        longDesc: 'Advisory support for amicable negotiation between parties, assistance with preparing settlement proposals, help with drafting settlement agreements, and explaining available legal options.',
        details: [
          'Advisory support for amicable negotiation between parties',
          'Assistance with preparing settlement proposals',
          'Help with drafting settlement agreements',
          'Explaining available legal options'
        ],
        note: 'Advisory and administrative support only. This is not a lawsuit, litigation, or legal practice. We do not represent clients in court.',
        
        stats: {
          consultationsHandled: 520,
          successRate: 94,
          avgTime: '5 days',
          clientSatisfaction: 4.7,
          experts: 8
        },
        features: [
          'Mediation support included',
          'Assistance with settlement agreement drafting',
          'Follow-up until resolution',
          'Confidential process'
        ],
        tags: ['Business', 'Individual', 'Banking'],
        testimonials: [
          { name: 'Khalid R.', rating: 5, comment: 'Quick resolution' },
          { name: 'Mona S.', rating: 4, comment: 'Very helpful' }
        ]
      },
      {
        id: 3,
        title: 'Debt Collection Support (Amicable)',
        icon: ReceiptIcon,
        color: '#4cc9f0',
        lightColor: '#d9f0ff',
        shortDesc: 'Administrative support for friendly debt collection',
        longDesc: 'Administrative support for communicating with debtor parties, assistance with sending payment notices, help with agreeing on debt scheduling, and administrative follow-up until settlement.',
        details: [
          'Support for communication with debtor parties',
          'Assistance with sending payment notices',
          'Help with agreeing on debt scheduling',
          'Administrative follow-up until settlement'
        ],
        note: 'Collection support is provided amicably and administratively only, not judicially. We do not represent clients in court.',
        
        stats: {
          consultationsHandled: 1250,
          successRate: 89,
          avgTime: '7 days',
          clientSatisfaction: 4.5,
          experts: 15
        },
        features: [
          'No collection, no fee option',
          'Payment plan structuring assistance',
          'Regular status updates',
          'Legal notice drafting assistance'
        ],
        tags: ['SMEs', 'Corporates', 'Individuals'],
        testimonials: [
          { name: 'Omar F.', rating: 5, comment: 'Recovered 100%' },
          { name: 'Layla M.', rating: 4, comment: 'Professional team' }
        ]
      },
      {
        id: 4,
        title: 'Legal Notices & Official Correspondence Support (Administrative)',
        icon: EmailIcon,
        color: '#fb5607',
        lightColor: '#ffe6d9',
        shortDesc: 'Administrative support for official correspondence',
        longDesc: 'Administrative assistance with preparing notifications, sending and receiving documents, administrative documentation of correspondence, and help with submitting requests on electronic systems.',
        details: [
          'Assistance with preparing notifications',
          'Administrative support for sending and receiving documents',
          'Administrative documentation of correspondence',
          'Help with submitting requests on electronic systems'
        ],
        note: 'This is an administrative support service, not legal or judicial practice. We do not provide legal advice or representation.',
        
        stats: {
          consultationsHandled: 2100,
          successRate: 99,
          avgTime: '1 day',
          clientSatisfaction: 4.8,
          experts: 10
        },
        features: [
          'Same-day service available',
          'Tracking and confirmation',
          'Multi-format support',
          'Official stamping included'
        ],
        tags: ['Corporate', 'Government', 'Personal'],
        testimonials: [
          { name: 'Noura A.', rating: 5, comment: 'Fast and accurate' },
          { name: 'Faisal H.', rating: 5, comment: 'Very reliable' }
        ]
      },
      {
        id: 5,
        title: 'Administrative Court Support Services (Non-representational)',
        icon: BusinessIcon,
        color: '#7209b7',
        lightColor: '#edd9ff',
        shortDesc: 'Administrative support for electronic file management',
        longDesc: 'Administrative assistance with opening electronic files based on client documents, assistance with extracting judgments, help with electronically tracking case status, and administrative support for preparing documents for licensed lawyers.',
        details: [
          'Administrative support for opening electronic files',
          'Assistance with extracting judgments',
          'Help with electronically tracking case status',
          'Administrative support for preparing documents for licensed lawyers'
        ],
        note: 'Does NOT include: Litigation, representation, memo writing, or court attendance. This is an administrative support service only.',
        
        stats: {
          consultationsHandled: 980,
          successRate: 96,
          avgTime: '2 days',
          clientSatisfaction: 4.8,
          experts: 14
        },
        features: [
          'Online case tracking assistance',
          'Document certification support',
          'Urgent processing available',
          'Integration with licensed lawyers'
        ],
        tags: ['Lawyers', 'Law Firms', 'Individuals'],
        testimonials: [
          { name: 'Dr. Ahmed', rating: 5, comment: 'Saved us hours' },
          { name: 'Sultan M.', rating: 4, comment: 'Efficient service' }
        ]
      }
    ],
    ar: [
      {
        id: 1,
        title: 'الاستشارات القانونية',
        icon: GavelIcon,
        color: '#4361ee',
        lightColor: '#e0e7ff',
        shortDesc: 'رأي قانوني متخصص بدون تمثيل قضائي',
        longDesc: 'تقديم الرأي القانوني، تحليل ودراسة المستندات، مراجعة العقود، وإيضاح الإجراءات القانونية للعملاء.',
        details: [
          'تقديم الرأي القانوني',
          'تحليل ودراسة المستندات',
          'مراجعة العقود',
          'إيضاح الإجراءات القانونية للعملاء'
        ],
        note: 'لا يشمل: الترافع، الحضور أمام المحاكم، تمثيل العملاء، أو إعداد مذكرات قانونية.',
        
        stats: {
          consultationsHandled: 850,
          successRate: 97,
          avgTime: '3 أيام',
          clientSatisfaction: 4.9,
          experts: 12
        },
        features: [
          'استشارات أونلاين 24/7',
          'تقييم أولي مجاني',
          'دعم متعدد اللغات',
          'سرية تامة'
        ],
        tags: ['شركات', 'أفراد', 'شركات ناشئة'],
        testimonials: [
          { name: 'أحمد م.', rating: 5, comment: 'خدمة ممتازة!' },
          { name: 'سارة ك.', rating: 5, comment: 'احترافية عالية' }
        ]
      },
      {
        id: 2,
        title: 'دعم تسوية المطالبات المالية (استشاري)',
        icon: AccountBalanceIcon,
        color: '#f72585',
        lightColor: '#ffe0f0',
        shortDesc: 'دعم استشاري لحل النزاعات بشكل ودي',
        longDesc: 'دعم استشاري للتفاوض الودي بين الأطراف، المساعدة في إعداد مقترحات التسوية، المساعدة في صياغة اتفاقيات التسوية، وبيان الخيارات القانونية المتاحة.',
        details: [
          'دعم استشاري للتفاوض الودي بين الأطراف',
          'المساعدة في إعداد مقترحات التسوية',
          'المساعدة في صياغة اتفاقيات تسوية',
          'بيان الخيارات القانونية المتاحة'
        ],
        note: 'الخدمة استشارية وإدارية فقط، وليست دعوى قضائية. لا نقوم بتمثيل العملاء أمام المحاكم.',
        
        stats: {
          consultationsHandled: 520,
          successRate: 94,
          avgTime: '5 أيام',
          clientSatisfaction: 4.7,
          experts: 8
        },
        features: [
          'دعم الوساطة',
          'المساعدة في صياغة اتفاقيات التسوية',
          'متابعة حتى التسوية',
          'سرية تامة'
        ],
        tags: ['أعمال', 'أفراد', 'بنوك'],
        testimonials: [
          { name: 'خالد ر.', rating: 5, comment: 'حل سريع' },
          { name: 'منى س.', rating: 4, comment: 'مفيد جداً' }
        ]
      },
      {
        id: 3,
        title: 'دعم تحصيل الديون (بالطرق الودية)',
        icon: ReceiptIcon,
        color: '#4cc9f0',
        lightColor: '#d9f0ff',
        shortDesc: 'دعم إداري لتحصيل الديون وجدولتها',
        longDesc: 'دعم إداري للتواصل مع الأطراف المدينة، المساعدة في إرسال إشعارات السداد، المساعدة في الاتفاق على جدولة الديون، والمتابعة الإدارية حتى التسوية.',
        details: [
          'دعم التواصل مع الأطراف المدينة',
          'المساعدة في إرسال إشعارات السداد',
          'المساعدة في الاتفاق على جدولة الديون',
          'المتابعة الإدارية حتى التسوية'
        ],
        note: 'دعم التحصيل يتم بشكل ودي وإداري فقط، وليس قضائياً. لا نقوم بتمثيل العملاء أمام المحاكم.',
        
        stats: {
          consultationsHandled: 1250,
          successRate: 89,
          avgTime: '7 أيام',
          clientSatisfaction: 4.5,
          experts: 15
        },
        features: [
          'خيار عدم التحصيل بدون رسوم',
          'المساعدة في هيكلة خطط السداد',
          'تحديثات دورية',
          'المساعدة في صياغة إشعارات قانونية'
        ],
        tags: ['شركات صغيرة', 'شركات كبرى', 'أفراد'],
        testimonials: [
          { name: 'عمر ف.', rating: 5, comment: 'استعدنا 100%' },
          { name: 'ليلى م.', rating: 4, comment: 'فريق محترف' }
        ]
      },
      {
        id: 4,
        title: 'دعم التبليغات والمراسلات الرسمية (إداري)',
        icon: EmailIcon,
        color: '#fb5607',
        lightColor: '#ffe6d9',
        shortDesc: 'دعم إداري للمراسلات الرسمية',
        longDesc: 'دعم إداري لإعداد الإخطارات، إرسال واستلام المستندات، التوثيق الإداري للمراسلات، والمساعدة في رفع الطلبات على الأنظمة الإلكترونية.',
        details: [
          'المساعدة في إعداد الإخطارات',
          'دعم إداري لإرسال واستلام المستندات',
          'التوثيق الإداري للمراسلات',
          'المساعدة في رفع الطلبات على الأنظمة الإلكترونية'
        ],
        note: 'الخدمة دعم إداري وليست قانونية قضائية. لا نقدم استشارات قانونية أو تمثيل.',
        
        stats: {
          consultationsHandled: 2100,
          successRate: 99,
          avgTime: '1 يوم',
          clientSatisfaction: 4.8,
          experts: 10
        },
        features: [
          'خدمة بنفس اليوم',
          'تتبع وإشعارات',
          'دعم متعدد الصيغ',
          'ختم رسمي'
        ],
        tags: ['شركات', 'حكومة', 'أفراد'],
        testimonials: [
          { name: 'نورا ع.', rating: 5, comment: 'سريع ودقيق' },
          { name: 'فيصل ه.', rating: 5, comment: 'موثوق جداً' }
        ]
      },
      {
        id: 5,
        title: 'خدمات دعم المحاكم الإدارية (بدون تمثيل)',
        icon: BusinessIcon,
        color: '#7209b7',
        lightColor: '#edd9ff',
        shortDesc: 'دعم إداري لإدارة الملفات إلكترونياً',
        longDesc: 'دعم إداري لفتح الملفات الإلكترونية بناء على مستندات العميل، المساعدة في استخراج الأحكام، المساعدة في متابعة حالة الدعوى إلكترونياً، والدعم الإداري لتجهيز المستندات للمحامين المرخصين.',
        details: [
          'دعم إداري لفتح الملفات الإلكترونية',
          'المساعدة في استخراج الأحكام',
          'المساعدة في متابعة حالة الدعوى إلكترونياً',
          'دعم إداري لتجهيز المستندات للمحامين المرخصين'
        ],
        note: 'لا يشمل: الترافع، التمثيل، كتابة المذكرات، أو الحضور أمام القضاء. هذه خدمة دعم إداري فقط.',
        
        stats: {
          consultationsHandled: 980,
          successRate: 96,
          avgTime: '2 أيام',
          clientSatisfaction: 4.8,
          experts: 14
        },
        features: [
          'المساعدة في تتبع القضايا أونلاين',
          'دعم توثيق المستندات',
          'خدمة عاجلة متاحة',
          'تكامل مع المحامين المرخصين'
        ],
        tags: ['محامون', 'مكاتب محاماة', 'أفراد'],
        testimonials: [
          { name: 'د. أحمد', rating: 5, comment: 'وفر علينا ساعات' },
          { name: 'سلطان م.', rating: 4, comment: 'خدمة فعالة' }
        ]
      }
    ]
  };

  // Office services data with updated terminology - using (دعم - مساعدة)
  const officeServices = {
    en: [
      { 
        title: 'Rental Dispute Applications (Administrative Support)', 
        icon: DescriptionIcon, 
        color: '#667eea',
        stats: { monthly: 150, satisfaction: 98 }
      },
      { 
        title: 'Government Transactions Support', 
        icon: AssignmentIcon, 
        color: '#764ba2',
        stats: { monthly: 220, satisfaction: 96 }
      },
      { 
        title: 'Form Filling Assistance', 
        icon: DescriptionIcon, 
        color: '#FE6B8B',
        stats: { monthly: 310, satisfaction: 99 }
      },
      { 
        title: 'Document Preparation Support', 
        icon: ArchiveIcon, 
        color: '#20c997',
        stats: { monthly: 180, satisfaction: 97 }
      },
      { 
        title: 'Attestation Services Support', 
        icon: VerifiedIcon, 
        color: '#f39c12',
        stats: { monthly: 200, satisfaction: 95 }
      },
      { 
        title: 'Document Archiving Support', 
        icon: ArchiveIcon, 
        color: '#e74c3c',
        stats: { monthly: 90, satisfaction: 99 }
      },
      { 
        title: 'Case Status Inquiry Assistance', 
        icon: SearchIcon, 
        color: '#1abc9c',
        stats: { monthly: 280, satisfaction: 100 }
      },
      { 
        title: 'Administrative Correspondence Support', 
        icon: ChatIcon, 
        color: '#3498db',
        stats: { monthly: 195, satisfaction: 97 }
      }
    ],
    ar: [
      { 
        title: 'دعم طلبات المنازعات الإيجارية في دبي(إداري)', 
        icon: DescriptionIcon, 
        color: '#667eea',
        stats: { monthly: 150, satisfaction: 98 }
      },
      { 
        title: 'دعم المعاملات الحكومية', 
        icon: AssignmentIcon, 
        color: '#764ba2',
        stats: { monthly: 220, satisfaction: 96 }
      },
      { 
        title: 'المساعدة في تعبئة النماذج', 
        icon: DescriptionIcon, 
        color: '#FE6B8B',
        stats: { monthly: 310, satisfaction: 99 }
      },
      { 
        title: 'دعم تجهيز المستندات', 
        icon: ArchiveIcon, 
        color: '#20c997',
        stats: { monthly: 180, satisfaction: 97 }
      },
      { 
        title: 'دعم خدمات التصديق', 
        icon: VerifiedIcon, 
        color: '#f39c12',
        stats: { monthly: 200, satisfaction: 95 }
      },
      { 
        title: 'دعم أرشفة المستندات', 
        icon: ArchiveIcon, 
        color: '#e74c3c',
        stats: { monthly: 90, satisfaction: 99 }
      },
      { 
        title: 'المساعدة في الاستعلام عن القضايا', 
        icon: SearchIcon, 
        color: '#1abc9c',
        stats: { monthly: 280, satisfaction: 100 }
      },
      { 
        title: 'دعم المراسلات الإدارية', 
        icon: ChatIcon, 
        color: '#3498db',
        stats: { monthly: 195, satisfaction: 97 }
      }
    ]
  };

  const currentServices = servicesData[currentLang];
  const currentOfficeServices = officeServices[currentLang];

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <Box sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '60vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          color: 'white',
          mb: { xs: 4, md: 8 },
          backgroundImage: 'url("https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80")',
          backgroundSize: 'cover',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          backgroundPosition: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(102,126,234,0.8) 100%)',
            zIndex: 1
          }
        }}
      >
        {/* Animated Background Elements - Adjusted for RTL */}
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 360, 0],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            position: 'absolute',
            top: '10%',
            right: isRTL ? 'auto' : '10%',
            left: isRTL ? '10%' : 'auto',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />

        <motion.div
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            bottom: '15%',
            left: isRTL ? 'auto' : '5%',
            right: isRTL ? '5%' : 'auto',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />

        {/* Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          >
            {/* Breadcrumbs */}
            <Breadcrumbs 
              aria-label="breadcrumb"
              sx={{ 
                justifyContent: 'center', 
                display: 'flex',
                mb: 3,
                '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.5)' }
              }}
            >
              <Link
                component="button"
                onClick={() => navigate('/')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  '&:hover': { color: 'white' }
                }}
              >
                <HomeIcon sx={{ fontSize: 16 }} />
                {currentLang === 'ar' ? 'الرئيسية' : 'Home'}
              </Link>
              <Typography sx={{ color: 'white', fontSize: '0.9rem' }}>
                {currentLang === 'ar' ? 'خدماتنا' : 'Our Services'}
              </Typography>
            </Breadcrumbs>

            {/* Main Title */}
            <motion.div
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  textShadow: '3px 3px 6px rgba(0,0,0,0.3)',
                  letterSpacing: '-0.02em'
                }}
              >
                {currentLang === 'ar' ? 'خدماتنا المتكاملة' : 'Our Comprehensive Services'}
              </Typography>
            </motion.div>

            {/* Description */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                color: 'rgba(255,255,255,0.9)',
                mb: 5,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                maxWidth: '800px',
                mx: 'auto',
                textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                fontWeight: 400
              }}
            >
              {currentLang === 'ar'
                ? 'نقدم مجموعة متكاملة من الخدمات الاستشارية القانونية والدعم الإداري'
                : 'We offer a comprehensive range of legal consultancy services and administrative support'}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Stats Overview - Updated terminology */}
      <Container maxWidth="lg" sx={{ mb: 6 }}>
        <Grid container spacing={3}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#667eea', color: 'white', borderRadius: '15px' }}>
              <PeopleIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">5,800+</Typography>
              <Typography variant="body2">{currentLang === 'ar' ? 'عميل سعيد' : 'Happy Clients'}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#764ba2', color: 'white', borderRadius: '15px' }}>
              <AssessmentIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">12,500+</Typography>
              <Typography variant="body2">{currentLang === 'ar' ? 'استشارة تمت' : 'Consultations Handled'}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#FE6B8B', color: 'white', borderRadius: '15px' }}>
              <VerifiedIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">98%</Typography>
              <Typography variant="body2">{currentLang === 'ar' ? 'نسبة نجاح' : 'Success Rate'}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#20c997', color: 'white', borderRadius: '15px' }}>
              <ScheduleIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">24/7</Typography>
              <Typography variant="body2">{currentLang === 'ar' ? 'دعم' : 'Support'}</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Main Services Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, pb: 8 }} id="services-section">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Section Title */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  fontWeight: 'bold',
                  color: '#1a1a2e',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 80,
                    height: 4,
                    background: 'linear-gradient(90deg, #667eea, #764ba2)',
                    borderRadius: 2
                  }
                }}
              >
                {currentLang === 'ar' ? 'خدماتنا الرئيسية' : 'Main Services'}
              </Typography>
            </Box>
          </motion.div>

          {/* Services Grid */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            {currentServices.map((service, index) => {
              const Icon = service.icon;
              const isExpanded = expandedId === service.id;
              
              return (
                <Grid item xs={12} md={6} lg={4} key={service.id}>
                  <motion.div
                    custom={index}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                    onHoverStart={() => setHoveredCard(service.id)}
                    onHoverEnd={() => setHoveredCard(null)}
                    style={{ height: '100%' }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative'
                      }}
                    >
                      {/* Colored Top Bar */}
                      <Box
                        sx={{
                          height: 8,
                          background: `linear-gradient(90deg, ${service.color}, ${service.color}dd)`
                        }}
                      />
                      
                      <CardContent sx={{ p: 3, flex: 1 }}>
                        {/* Header with Icon and Title */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
                          <Avatar
                            sx={{
                              width: 60,
                              height: 60,
                              bgcolor: service.lightColor,
                              color: service.color,
                              border: `2px solid ${service.color}`
                            }}
                          >
                            <Icon sx={{ fontSize: 30 }} />
                          </Avatar>
                          
                          <Typography
                            variant="h5"
                            sx={{
                              fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                              fontWeight: 'bold',
                              color: '#1a1a2e'
                            }}
                          >
                            {service.title}
                          </Typography>
                        </Box>

                        {/* Tags */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, justifyContent: direction === 'rtl' ? 'flex-end' : 'flex-start' }}>
                          {service.tags.map((tag, idx) => (
                            <Chip
                              key={idx}
                              label={tag}
                              size="small"
                              sx={{
                                bgcolor: service.lightColor,
                                color: service.color,
                                fontSize: '0.7rem'
                              }}
                            />
                          ))}
                        </Box>

                        {/* Stats Row - Updated to show consultations instead of cases */}
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Tooltip title={currentLang === 'ar' ? 'عدد الاستشارات' : 'Consultations Handled'}>
                              <Paper sx={{ p: 1, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                                <TrendingUpIcon sx={{ fontSize: 16, color: service.color }} />
                                <Typography variant="caption" display="block">
                                  {service.stats.consultationsHandled}+
                                </Typography>
                              </Paper>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={6}>
                            <Tooltip title={currentLang === 'ar' ? 'نسبة النجاح' : 'Success Rate'}>
                              <Paper sx={{ p: 1, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                                <ThumbUpIcon sx={{ fontSize: 16, color: service.color }} />
                                <Typography variant="caption" display="block">
                                  {service.stats.successRate}%
                                </Typography>
                              </Paper>
                            </Tooltip>
                          </Grid>
                        </Grid>

                        {/* Short Description */}
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            color: '#666',
                            lineHeight: 1.8,
                            mb: 2,
                            textAlign: direction === 'rtl' ? 'right' : 'left'
                          }}
                        >
                          {service.shortDesc}
                        </Typography>

                        {/* Long Description */}
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            color: '#4a4a4a',
                            lineHeight: 1.8,
                            mb: 3,
                            fontStyle: 'italic',
                            borderLeft: direction === 'rtl' ? 'none' : `3px solid ${service.color}`,
                            borderRight: direction === 'rtl' ? `3px solid ${service.color}` : 'none',
                            pl: direction === 'rtl' ? 0 : 2,
                            pr: direction === 'rtl' ? 2 : 0,
                            py: 1,
                            bgcolor: '#f8f9fa',
                            borderRadius: '5px',
                            textAlign: direction === 'rtl' ? 'right' : 'left'
                          }}
                        >
                          {service.longDesc}
                        </Typography>

                        {/* Toggle Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                          <Button
                            onClick={() => toggleExpand(service.id)}
                            size="small"
                            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{
                              color: service.color,
                              fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                              '&:hover': {
                                bgcolor: service.lightColor
                              }
                            }}
                          >
                            {isExpanded 
                              ? (currentLang === 'ar' ? 'إخفاء التفاصيل' : 'Hide Details')
                              : (currentLang === 'ar' ? 'عرض التفاصيل' : 'View Details')}
                          </Button>
                        </Box>

                        {/* Collapsible Details */}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ mt: 2 }}>
                            {/* Features */}
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                fontWeight: 'bold',
                                color: service.color,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                justifyContent: direction === 'rtl' ? 'flex-end' : 'flex-start'
                              }}
                            >
                              <CheckCircleIcon fontSize="small" />
                              {currentLang === 'ar' ? 'المميزات:' : 'Features:'}
                            </Typography>

                            <Grid container spacing={1} sx={{ mb: 3 }}>
                              {service.features.map((feature, idx) => (
                                <Grid item xs={12} key={idx}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                      p: 1,
                                      bgcolor: service.lightColor,
                                      borderRadius: '8px',
                                      flexDirection: direction === 'rtl' ? 'row-reverse' : 'row'
                                    }}
                                  >
                                    <CheckCircleIcon sx={{ color: service.color, fontSize: 16 }} />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                        color: '#1a1a2e'
                                      }}
                                    >
                                      {feature}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Testimonials */}
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                fontWeight: 'bold',
                                color: service.color,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                justifyContent: direction === 'rtl' ? 'flex-end' : 'flex-start'
                              }}
                            >
                              <StarIcon fontSize="small" />
                              {currentLang === 'ar' ? 'آراء العملاء:' : 'Client Testimonials:'}
                            </Typography>

                            <Grid container spacing={1} sx={{ mb: 3 }}>
                              {service.testimonials.map((testimonial, idx) => (
                                <Grid item xs={12} key={idx}>
                                  <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        {testimonial.name}
                                      </Typography>
                                      <Rating value={testimonial.rating} size="small" readOnly />
                                    </Box>
                                    <Typography variant="caption" sx={{ color: '#666', display: 'block', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                      "{testimonial.comment}"
                                    </Typography>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Important Note */}
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                bgcolor: '#fff3f5',
                                borderRadius: '10px',
                                border: `1px solid ${service.color}30`
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                  color: service.color,
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 1,
                                  justifyContent: direction === 'rtl' ? 'flex-end' : 'flex-start'
                                }}
                              >
                                <WarningIcon fontSize="small" />
                                {currentLang === 'ar' ? 'ملاحظة مهمة:' : 'Important Note:'}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                  color: '#666',
                                  lineHeight: 1.6,
                                  fontSize: '0.8rem',
                                  textAlign: direction === 'rtl' ? 'right' : 'left'
                                }}
                              >
                                {service.note}
                              </Typography>
                            </Paper>
                          </Box>
                        </Collapse>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>

          {/* Office Services Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  fontWeight: 'bold',
                  color: '#1a1a2e',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 80,
                    height: 4,
                    background: 'linear-gradient(90deg, #FE6B8B, #FF8E53)',
                    borderRadius: 2
                  }
                }}
              >
                {currentLang === 'ar' ? 'خدمات الدعم الإداري' : 'Administrative Support Services'}
              </Typography>
            </Box>
          </motion.div>

          {/* Office Services Grid */}
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {currentOfficeServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{
                      y: -8,
                      scale: 1.02,
                      boxShadow: '0 15px 30px rgba(0,0,0,0.15)'
                    }}
                    style={{ height: '100%' }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        p: 3,
                        height: '100%',
                        minHeight: 180,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        background: 'white',
                        borderRadius: '15px',
                        border: `1px solid ${service.color}30`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: service.color,
                          '& .office-icon': {
                            transform: 'scale(1.1) rotate(5deg)'
                          }
                        }
                      }}
                    >
                      <Icon
                        className="office-icon"
                        sx={{
                          fontSize: 40,
                          color: service.color,
                          mb: 1,
                          transition: 'transform 0.3s ease'
                        }}
                      />
                      
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                          color: '#1a1a2e',
                          fontWeight: 600,
                          lineHeight: 1.6,
                          mb: 1
                        }}
                      >
                        {service.title}
                      </Typography>

                      {/* Additional Info */}
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 1 }}>
                        <Tooltip title={currentLang === 'ar' ? 'شهرياً' : 'Monthly'}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimelineIcon sx={{ fontSize: 14, color: service.color }} />
                            <Typography variant="caption">{service.stats.monthly}+</Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip title={currentLang === 'ar' ? 'رضا العملاء' : 'Satisfaction'}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ThumbUpIcon sx={{ fontSize: 14, color: service.color }} />
                            <Typography variant="caption">{service.stats.satisfaction}%</Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>

          {/* Important Note - Legal Disclaimer */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                background: 'linear-gradient(135deg, #fff3f5 0%, #ffe9ec 100%)',
                borderRadius: '20px',
                border: '1px solid #ffcdd2',
                mb: 4
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                  <motion.div
                    animate={{
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <WarningIcon sx={{ fontSize: 70, color: '#e83e8c' }} />
                  </motion.div>
                </Grid>
                <Grid item xs={12} md={10}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      color: '#e83e8c',
                      mb: 2,
                      textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}
                  >
                    ⚠️ {currentLang === 'ar' ? 'إخلاء المسؤولية القانوني' : 'Legal Disclaimer'}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      color: '#4a4a4a',
                      lineHeight: 1.8,
                      textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}
                  >
                    {currentLang === 'ar'
                      ? 'تلتزم الماريه للإستشارات القانونيه بممارسة نشاطها وفق ما هو مسموح به في الرخصة الصادرة من Creative City – الفجيرة. تؤكد الشركة أنها لا تمارس أعمال المحاماة أو أي نشاط يتطلب موافقات خارجية من الجهات القضائية أو وزارة العدل. جميع الخدمات تقتصر على الاستشارات القانونية والخدمات الإدارية فقط دون تمثيل العملاء أو الترافع أمام المحاكم.'
                      : 'Almaryah Legal Consultancy is committed to operating in accordance with the license issued by Creative City – Fujairah. The company confirms that it does not practice law or any activity requiring external approvals from judicial authorities or the Ministry of Justice. All services are limited to legal consultations and administrative services only without client representation or court litigation.'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>

          {/* Back to Home Button */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBackIcon sx={{ transform: direction === 'rtl' ? 'rotate(180deg)' : 'none' }} />}
                onClick={() => navigate('/')}
                sx={{
                  color: '#667eea',
                  borderColor: '#667eea',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  fontWeight: 600,
                  borderRadius: '50px',
                  '&:hover': {
                    borderColor: '#764ba2',
                    backgroundColor: 'rgba(118,75,162,0.1)'
                  }
                }}
              >
                {currentLang === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default OurServicesPage;